import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * useAuthStatus — one-shot probe to /api/auth/status describing which
 * sign-in methods are live. Returns
 *   { loading, enabled, magicLinkEnabled }
 * `enabled`          — any sign-in available (email+password is always on).
 * `magicLinkEnabled` — "email me a link" option (only once Resend is live).
 *
 * Cached on `window` so every mount after the first is instant. Email +
 * password works without any email provider, so we default `enabled` to true
 * even if the probe fails — the Sign-In UI should stay available.
 */
const CACHE_KEY = "__medskin_auth_status";

export function useAuthStatus() {
  const cached = typeof window !== "undefined" ? window[CACHE_KEY] : undefined;
  const [state, setState] = useState(
    cached !== undefined
      ? { loading: false, ...cached }
      : { loading: true, enabled: true, magicLinkEnabled: false },
  );

  useEffect(() => {
    if (cached !== undefined) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/status");
        const val = {
          enabled: data?.enabled !== false,
          magicLinkEnabled: Boolean(data?.magic_link_enabled),
        };
        if (typeof window !== "undefined") window[CACHE_KEY] = val;
        if (alive) setState({ loading: false, ...val });
      } catch {
        const val = { enabled: true, magicLinkEnabled: false };
        if (typeof window !== "undefined") window[CACHE_KEY] = val;
        if (alive) setState({ loading: false, ...val });
      }
    })();
    return () => { alive = false; };
  }, [cached]);

  return state;
}
